import React, { useState } from 'react';
import { ExpenseEntry, EXPENSE_CATEGORIES, EXPENSE_COGS_TYPES, BUSINESS_CATEGORIES, CategoryRule, RuleMatchType } from '../types';
import TagManager from './TagManager';

interface ExpenseTableProps {
  expenses: ExpenseEntry[];
  onUpdateExpense: (index: number, updatedExpense: ExpenseEntry) => void;
  onAddCategoryRule?: (rule: CategoryRule) => void;
}

const ExpenseTable: React.FC<ExpenseTableProps> = ({ 
  expenses, 
  onUpdateExpense,
  onAddCategoryRule 
}) => {
  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);
  const [batchCategory, setBatchCategory] = useState<string>(EXPENSE_CATEGORIES[0]);
  const [batchExpenseCOGS, setBatchExpenseCOGS] = useState<string>(EXPENSE_COGS_TYPES[0]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [expenseCOGSFilter, setExpenseCOGSFilter] = useState<string>('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showTagsColumn, setShowTagsColumn] = useState(true);
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [ruleTransaction, setRuleTransaction] = useState<ExpenseEntry | null>(null);
  const [rulePattern, setRulePattern] = useState('');
  const [ruleMatchType, setRuleMatchType] = useState(RuleMatchType.CONTAINS);
  const [ruleCaseSensitive, setRuleCaseSensitive] = useState(false);
  const [expandedRowIndex, setExpandedRowIndex] = useState<number | null>(null);
  
  if (expenses.length === 0) {
    return null;
  }

  const handleCategoryChange = (index: number, category: string) => {
    const expense = expenses[index];
    onUpdateExpense(index, { ...expense, category });
  };

  const handleExpenseCOGSChange = (index: number, expenseCOGS: string) => {
    const expense = expenses[index];
    onUpdateExpense(index, { ...expense, expenseCOGS });
  };

  const handleNotesChange = (index: number, notes: string) => {
    const expense = expenses[index];
    onUpdateExpense(index, { ...expense, notes });
  };

  const handleTaxDeductibleChange = (index: number, taxDeductible: boolean) => {
    const expense = expenses[index];
    onUpdateExpense(index, { ...expense, taxDeductible });
  };

  const handleUpdateTags = (index: number, tags: string[]) => {
    const expense = expenses[index];
    onUpdateExpense(index, { ...expense, tags });
  };
  
  const handleSelectRow = (index: number) => {
    if (selectedIndices.includes(index)) {
      setSelectedIndices(selectedIndices.filter(i => i !== index));
    } else {
      setSelectedIndices([...selectedIndices, index]);
    }
  };
  
  const handleSelectAll = () => {
    if (selectedIndices.length === filteredExpenses.length) {
      setSelectedIndices([]);
    } else {
      setSelectedIndices(filteredExpenses.map((_, index) => index));
    }
  };
  
  const handleBatchCategoryChange = () => {
    selectedIndices.forEach(index => {
      const expense = expenses[index];
      onUpdateExpense(index, { ...expense, category: batchCategory });
    });
  };

  const handleBatchExpenseCOGSChange = () => {
    selectedIndices.forEach(index => {
      const expense = expenses[index];
      onUpdateExpense(index, { ...expense, expenseCOGS: batchExpenseCOGS });
    });
  };

  const handleBatchAssign = () => {
    selectedIndices.forEach(index => {
      const expense = expenses[index];
      onUpdateExpense(index, { 
        ...expense, 
        category: batchCategory,
        expenseCOGS: batchExpenseCOGS
      });
    });
    setSelectedIndices([]);
  };

  const handleCreateRuleFromTransaction = (expense: ExpenseEntry) => {
    setRuleTransaction(expense);
    // Extract a good default pattern from the description
    let defaultPattern = expense.description;
    
    // Remove numbers and special chars for a better default pattern
    if (expense.description.includes('*')) {
      const parts = expense.description.split('*');
      if (parts.length > 1) {
        defaultPattern = parts[1].trim().split(' ')[0];
      }
    } else {
      // Take the first word that's not a number
      const words = expense.description.split(/\s+/);
      defaultPattern = words.find(word => !word.match(/^\d+$/)) || words[0] || '';
    }
    
    setRulePattern(defaultPattern);
    setShowRuleModal(true);
  };
  
  const handleSaveRule = () => {
    if (!ruleTransaction || !rulePattern.trim() || !onAddCategoryRule) return;
    
    const newRule: CategoryRule = {
      pattern: rulePattern,
      category: ruleTransaction.category,
      expenseCOGS: ruleTransaction.expenseCOGS,
      matchType: ruleMatchType,
      caseSensitive: ruleCaseSensitive
    };
    
    onAddCategoryRule(newRule);
    setShowRuleModal(false);
    setRuleTransaction(null);
    setRulePattern('');
  };

  const handleToggleRowExpansion = (index: number) => {
    if (expandedRowIndex === index) {
      setExpandedRowIndex(null);
    } else {
      setExpandedRowIndex(index);
    }
  };

  // Get all unique tags across all expenses
  const allTags = Array.from(new Set(
    expenses.flatMap(expense => expense.tags || [])
  )).sort();

  // Get unique categories and expense/COGS values
  const uniqueCategories = Array.from(new Set(expenses.map(e => e.category))).sort();
  const uniqueExpenseCOGS = Array.from(new Set(expenses.map(e => e.expenseCOGS))).sort();

  // First filter by search term
  const searchFiltered = expenses.filter(expense => 
    !searchTerm || 
    expense.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    expense.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
    expense.expenseCOGS.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (expense.tags && expense.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())))
  );
  
  // Then filter by category
  const categoryFiltered = categoryFilter 
    ? searchFiltered.filter(expense => expense.category === categoryFilter)
    : searchFiltered;

  // Then filter by expense/COGS
  const expenseCOGSFiltered = expenseCOGSFilter
    ? categoryFiltered.filter(expense => expense.expenseCOGS === expenseCOGSFilter)
    : categoryFiltered;

  // Then filter by tag if set
  const filteredExpenses = expenseCOGSFiltered;

  // Calculate counts
  const categoryCounts = expenses.reduce((counts, expense) => {
    counts[expense.category] = (counts[expense.category] || 0) + 1;
    return counts;
  }, {} as Record<string, number>);

  const expenseCOGSCounts = expenses.reduce((counts, expense) => {
    counts[expense.expenseCOGS] = (counts[expense.expenseCOGS] || 0) + 1;
    return counts;
  }, {} as Record<string, number>);

  return (
    <div className="mt-8">
      <div className="mb-4 flex flex-col space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="sm:w-1/3">
            <label htmlFor="search" className="sr-only">Search</label>
            <div className="relative rounded-md shadow-sm">
              <input
                type="text"
                name="search"
                id="search"
                className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-4 pr-12 sm:text-sm border-gray-300 rounded-md"
                placeholder="Search expenses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row sm:space-x-4 space-y-2 sm:space-y-0">
            <div className="flex space-x-2">
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="block rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm"
              >
                <option value="">All Categories</option>
                {uniqueCategories.map((category) => (
                  <option key={category} value={category}>
                    {category} ({categoryCounts[category]})
                  </option>
                ))}
              </select>
              
              {categoryFilter && (
                <button
                  onClick={() => setCategoryFilter('')}
                  className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Clear
                </button>
              )}
            </div>
            
            <div className="flex space-x-2">
              <select
                value={expenseCOGSFilter}
                onChange={(e) => setExpenseCOGSFilter(e.target.value)}
                className="block rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm"
              >
                <option value="">All Expense/COGS</option>
                {uniqueExpenseCOGS.map((expenseCOGS) => (
                  <option key={expenseCOGS} value={expenseCOGS}>
                    {expenseCOGS} ({expenseCOGSCounts[expenseCOGS]})
                  </option>
                ))}
              </select>
              
              {expenseCOGSFilter && (
                <button
                  onClick={() => setExpenseCOGSFilter('')}
                  className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
        
        {selectedIndices.length > 0 && (
          <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:items-center bg-gray-50 p-3 rounded-md">
            <span className="text-sm text-gray-700 mr-4">
              {selectedIndices.length} item{selectedIndices.length !== 1 ? 's' : ''} selected
            </span>
            
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
              <div className="flex space-x-2 items-center">
                <select
                  value={batchCategory}
                  onChange={(e) => setBatchCategory(e.target.value)}
                  className="block rounded-md border-gray-300 py-2 pl-3 pr-10 text-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
                >
                  <option value="" disabled>Select Category</option>
                  {BUSINESS_CATEGORIES.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
                
                <button
                  type="button"
                  onClick={handleBatchCategoryChange}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Set Category
                </button>
              </div>
              
              <div className="flex space-x-2 items-center">
                <select
                  value={batchExpenseCOGS}
                  onChange={(e) => setBatchExpenseCOGS(e.target.value)}
                  className="block rounded-md border-gray-300 py-2 pl-3 pr-10 text-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
                >
                  <option value="" disabled>Select Expense/COGS</option>
                  {EXPENSE_COGS_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
                
                <button
                  type="button"
                  onClick={handleBatchExpenseCOGSChange}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Set Expense/COGS
                </button>
              </div>
              
              <div className="flex space-x-2 items-center">
                <button
                  type="button"
                  onClick={handleBatchAssign}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  Apply Both
                </button>
                
                <button
                  type="button"
                  onClick={() => setSelectedIndices([])}
                  className="text-sm text-gray-700 hover:text-gray-900"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      
      <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="relative py-3.5 pl-4 pr-3">
                <input
                  type="checkbox"
                  className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  checked={selectedIndices.length === filteredExpenses.length && filteredExpenses.length > 0}
                  onChange={handleSelectAll}
                />
              </th>
              <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">
                Date
              </th>
              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                Description
              </th>
              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                Amount
              </th>
              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                Category
              </th>
              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                Expense/COGS
              </th>
              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                Tax Deductible
              </th>
              {showTagsColumn && (
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                  Tags
                </th>
              )}
              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                Notes
              </th>
              {onAddCategoryRule && (
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {filteredExpenses.map((expense, index) => (
              <React.Fragment key={index}>
                <tr 
                  className={`${selectedIndices.includes(index) ? "bg-blue-50" : ""} ${expandedRowIndex === index ? "border-b-0" : ""}`}
                >
                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      checked={selectedIndices.includes(index)}
                      onChange={() => handleSelectRow(index)}
                    />
                  </td>
                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm text-gray-900">
                    {expense.date}
                  </td>
                  <td 
                    className="max-w-md px-3 py-4 text-sm text-gray-500 cursor-pointer hover:text-blue-600"
                    onClick={() => handleToggleRowExpansion(index)}
                  >
                    <div className="truncate">{expense.description}</div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    ${expense.amount.toFixed(2)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                    <select
                      value={expense.category}
                      onChange={(e) => handleCategoryChange(index, e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm"
                    >
                      {BUSINESS_CATEGORIES.map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                    <select
                      value={expense.expenseCOGS}
                      onChange={(e) => handleExpenseCOGSChange(index, e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm"
                    >
                      {EXPENSE_COGS_TYPES.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                    <label className="inline-flex items-center">
                      <input
                        type="checkbox"
                        checked={expense.taxDeductible || false}
                        onChange={(e) => handleTaxDeductibleChange(index, e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-gray-700">Tax Deductible</span>
                    </label>
                  </td>
                  {showTagsColumn && (
                    <td className="px-3 py-4 text-sm">
                      <div className="flex flex-wrap gap-1">
                        {(expense.tags || []).map(tag => (
                          <span 
                            key={tag} 
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                          >
                            {tag}
                          </span>
                        ))}
                        <button
                          type="button"
                          onClick={() => handleToggleRowExpansion(index)}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          {(expense.tags || []).length === 0 ? 'Add Tags' : 'Edit'}
                        </button>
                      </div>
                    </td>
                  )}
                  <td className="px-3 py-4 text-sm">
                    <input
                      type="text"
                      value={expense.notes}
                      onChange={(e) => handleNotesChange(index, e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 py-2 pl-3 text-base focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm"
                      placeholder="Add notes..."
                    />
                  </td>
                  {onAddCategoryRule && (
                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                      <button
                        type="button"
                        onClick={() => handleCreateRuleFromTransaction(expense)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Create Rule
                      </button>
                    </td>
                  )}
                </tr>
                {expandedRowIndex === index && (
                  <tr className="bg-blue-50">
                    <td colSpan={10} className="px-8 py-4">
                      <div className="pb-4">
                        <h4 className="text-sm font-medium text-gray-900 mb-2">
                          Tags for this expense
                        </h4>
                        <TagManager
                          expense={expense}
                          onUpdateTags={(tags) => handleUpdateTags(index, tags)}
                        />
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-2">
                          Full Description
                        </h4>
                        <p className="text-sm text-gray-700">{expense.description}</p>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Rule Creation Modal */}
      {showRuleModal && ruleTransaction && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowRuleModal(false)}></div>

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <div className="mt-3 text-center sm:mt-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Create Rule from Transaction
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      This will create a new rule to automatically categorize similar transactions.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-5 sm:mt-6">
                <div className="mb-4">
                  <label htmlFor="transaction-desc" className="block text-sm font-medium text-gray-700">
                    Transaction Description
                  </label>
                  <input
                    type="text"
                    id="transaction-desc"
                    value={ruleTransaction.description}
                    disabled
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-100 text-gray-700 sm:text-sm"
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="rule-pattern" className="block text-sm font-medium text-gray-700">
                    Pattern to Match
                  </label>
                  <input
                    type="text"
                    id="rule-pattern"
                    value={rulePattern}
                    onChange={(e) => setRulePattern(e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-gray-900 sm:text-sm"
                    placeholder="Enter keywords to match"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Enter the text pattern that should be matched in similar transactions.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="rule-category" className="block text-sm font-medium text-gray-700">
                      Category
                    </label>
                    <input
                      type="text"
                      id="rule-category"
                      value={ruleTransaction.category}
                      disabled
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-100 text-gray-700 sm:text-sm"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="rule-expense-cogs" className="block text-sm font-medium text-gray-700">
                      Expense/COGS
                    </label>
                    <input
                      type="text"
                      id="rule-expense-cogs"
                      value={ruleTransaction.expenseCOGS}
                      disabled
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-100 text-gray-700 sm:text-sm"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label htmlFor="rule-match-type" className="block text-sm font-medium text-gray-700">
                    Match Type
                  </label>
                  <select
                    id="rule-match-type"
                    value={ruleMatchType}
                    onChange={(e) => setRuleMatchType(e.target.value as RuleMatchType)}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-gray-900 sm:text-sm"
                  >
                    <option value={RuleMatchType.CONTAINS}>Contains</option>
                    <option value={RuleMatchType.EXACT}>Exact Match</option>
                    <option value={RuleMatchType.STARTS_WITH}>Starts With</option>
                    <option value={RuleMatchType.ENDS_WITH}>Ends With</option>
                    <option value={RuleMatchType.REGEX}>Regular Expression</option>
                  </select>
                </div>

                <div className="mb-4">
                  <div className="flex items-center">
                    <input
                      id="case-sensitive"
                      type="checkbox"
                      checked={ruleCaseSensitive}
                      onChange={(e) => setRuleCaseSensitive(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor="case-sensitive" className="ml-2 block text-sm text-gray-700">
                      Case Sensitive
                    </label>
                  </div>
                </div>

                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowRuleModal(false)}
                    className="flex-1 inline-flex justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveRule}
                    className="flex-1 inline-flex justify-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Create Rule
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpenseTable; 